import { useEffect, useMemo, useRef, useState } from 'react';

import { getIllustrationTypicalTime } from '@shared/illustration-download-typical-times';

import type { FundingPolicyType } from '@shared';

const MAX_PROGRESS_PERCENT = 90;

interface UseIllustrationProgressOptions {
  isGenerating: boolean;
  fundingPolicyType: FundingPolicyType | null | undefined;
}

interface IllustrationProgressState {
  elapsedSeconds: number;
  typicalSeconds: number;
  progressPercent: number;
  isOvertime: boolean;
  overtimeSeconds: number;
}

export function useIllustrationProgress({
  isGenerating,
  fundingPolicyType,
}: UseIllustrationProgressOptions): IllustrationProgressState {
  const typicalSeconds = useMemo(() => getIllustrationTypicalTime(fundingPolicyType), [fundingPolicyType]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isGenerating) {
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
        setElapsedSeconds(0);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (startTimeRef.current !== null) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startTimeRef.current = null;
      setElapsedSeconds(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isGenerating]);

  const isOvertime = elapsedSeconds > typicalSeconds;
  const overtimeSeconds = isOvertime ? elapsedSeconds - typicalSeconds : 0;
  const progressPercent = Math.min((elapsedSeconds / typicalSeconds) * 100, MAX_PROGRESS_PERCENT);

  return {
    elapsedSeconds,
    typicalSeconds,
    progressPercent,
    isOvertime,
    overtimeSeconds,
  };
}
